import axios from 'axios';

// Create axios instance with default config
const adminApi = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window._API_BASE : undefined) || 'http://localhost:5001',
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token
adminApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle token expiration and errors
adminApi.interceptors.response.use(
    (response) => {
        console.log('API Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
        return response;
    },
    (error) => {
        console.error('API Error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            config: {
                headers: error.config?.headers,
                method: error.config?.method,
                data: error.config?.data
            }
        });

        if (error.response?.status === 401) {
            console.warn('Authentication error - Token might be invalid or expired');
            // Don't clear token here to avoid UI flicker
            // The component will handle the 401 error appropriately
        }

        return Promise.reject(error);
    }
);

// Admin Authentication
export const adminLogin = async (credentials) => {
    try {
        console.log('Attempting admin login with email:', credentials?.email);

        const response = await adminApi.post('/auth/admin/login', credentials, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        console.log('Login response received:', {
            status: response.status,
            hasToken: !!response.data?.token,
            hasAdminData: !!response.data?.admin
        });

        if (response.data?.token) {
            localStorage.setItem('adminToken', response.data.token);
            console.log('Admin token stored in localStorage');

            // Also store admin data if available
            if (response.data.admin) {
                localStorage.setItem('adminData', JSON.stringify(response.data.admin));
            }
        } else {
            console.warn('No token received in login response');
        }

        return response.data;

    } catch (error) {
        console.error('Login error details:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            request: error.request ? 'Request was made but no response received' : 'No request was made'
        });

        // Handle different types of errors
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const { status, data } = error.response;

            if (status === 401) {
                throw new Error(data?.error || 'Invalid email or password');
            } else if (status === 403) {
                throw new Error(data?.error || 'Account not authorized');
            } else if (data?.error) {
                throw new Error(data.error);
            } else {
                throw new Error(`Server error: ${status}`);
            }
        } else if (error.request) {
            // The request was made but no response was received
            throw new Error('No response from server. Please check your internet connection and try again.');
        } else {
            // Something happened in setting up the request that triggered an Error
            throw new Error(`Login failed: ${error.message}`);
        }
    }
};

export const adminLogout = async () => {
    try {
        // Get the token before removing it
        const token = localStorage.getItem('adminToken');

        // If there's no token, just return success
        if (!token) {
            return { message: 'Already logged out' };
        }

        // Make the API call to logout
        const response = await adminApi.post('/auth/admin/logout');

        // Clear the token from localStorage
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminRole');

        return response.data;
    } catch (error) {
        // Even if the server request fails, we should still clear the token
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminRole');
        throw error.response?.data || error.message;
    }
};

export const adminForgotPassword = async (email) => {
    try {
        const response = await adminApi.post('/auth/admin/forgot-password', { email });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const adminResetPassword = async (token, newPassword) => {
    try {
        const response = await adminApi.post('/auth/admin/reset-password', {
            token,
            newPassword
        });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw error;
        } else if (error.request) {
            throw new Error('No response from server. Please try again later.');
        } else {
            throw new Error('An error occurred. Please try again later.');
        }
    }
};

// Admin Profile
export const getAdminProfile = async () => {
    try {
        const response = await adminApi.get('/auth/admin/profile');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateAdminProfile = async (profileData) => {
    try {
        const response = await adminApi.put('/auth/admin/profile', profileData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateAdminPassword = async (passwordData) => {
    try {
        const response = await adminApi.put('/auth/admin/change-password', passwordData);
        return response.data;
    } catch (error) {
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error(error.message || 'Failed to update password');
    }
};

// Admin Settings
export const getAdminSettings = async () => {
    try {
        const response = await adminApi.get('/auth/admin/settings');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateAdminSettings = async (settings) => {
    try {
        const response = await adminApi.put('/auth/admin/settings', settings);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const sendTestEmail = async () => {
    try {
        const response = await adminApi.post('/auth/admin/settings/test-email');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// User Management
export const getAllUsers = async (filters = {}) => {
    try {
        console.log('Fetching users with filters:', filters);

        // Get the token from localStorage
        const token = localStorage.getItem('adminToken');
        console.log('Using token from localStorage:', token ? 'Token exists' : 'No token found');

        if (!token) {
            throw new Error('No authentication token found. Please log in again.');
        }

        // Make sure the token is valid JWT format
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
            console.error('Invalid token format');
            throw new Error('Invalid authentication token format');
        }

        // Decode the token to log the payload (for debugging)
        try {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('Token payload:', payload);
        } catch (e) {
            console.warn('Could not decode token payload:', e.message);
        }

        // Make the request with the token in the Authorization header
        const response = await adminApi.get('/auth/admin/users', {
            params: filters,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        console.log('Users response received. Status:', response.status);
        return response.data;

    } catch (error) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            response: error.response?.data,
            config: {
                url: error.config?.url,
                method: error.config?.method,
                headers: {
                    ...error.config?.headers,
                    // Don't log the full token for security
                    Authorization: error.config?.headers?.Authorization ? 'Bearer [REDACTED]' : undefined
                },
                data: error.config?.data
            }
        };

        console.error('Error in getAllUsers:', errorDetails);

        // Return a more user-friendly error message
        if (error.response?.status === 401) {
            throw new Error('Your session has expired. Please log in again.');
        } else if (error.response?.status === 403) {
            throw new Error('You do not have permission to view this resource.');
        } else if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        } else if (error.message) {
            throw error;
        } else {
            throw new Error('Failed to fetch users. Please try again later.');
        }
    }
};

export const addUser = async (userData) => {
    try {
        const payload = {
            name: userData.name,
            // Send both keys for maximum compatibility with backend
            emailId: userData.emailId || userData.email,
            email: userData.email || userData.emailId,
            password: userData.password,
            // Backend requires a username; derive from input or email local-part
            username: userData.username
                || (userData.name ? userData.name.trim().toLowerCase().replace(/\s+/g, '') : '')
                || (userData.emailId || userData.email || '').split('@')[0],
            // Backend commonly expects `user_type` = 'viewer' | 'creator'
            user_type: userData.role === 'creator' || userData.role === 'creator_pro' ? 'creator' : 'viewer',
            // Keep role too in case backend accepts it
            role: userData.role || 'user',
            isActive: true,
            isVerified: true,
            membershipPlan: userData.role === 'creator_pro' ? 'pro' : 'free',
        };
        const response = await adminApi.post('/auth/admin/users', payload);
        return response.data;
    } catch (error) {
        const msg = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Registration failed';
        throw new Error(msg);
    }
};

export const updateUser = async (email, userData) => {
    try {
        // Ensure email is properly encoded for URL
        const encodedEmail = encodeURIComponent(email);

        // Map frontend user data to backend expected format
        const role = userData?.role;
        const backendUserData = {
            name: userData.name,
            isActive: userData.isActive,
            // Map frontend role to backend user_type values
            ...(role !== undefined ? { user_type: (role === 'creator' || role === 'creator_pro') ? 'creator' : 'viewer' } : {}),
            isVerified: userData.isVerified,
            // Persist membership plan for creator pro
            ...(role === 'creator_pro' ? { membershipPlan: 'pro' } : role === 'creator' ? { membershipPlan: 'free' } : {}),
            // If email is being changed, include the new email in the payload
            ...(userData.email && userData.email !== email ? { email: userData.email, emailId: userData.email } : { emailId: email })
        };

        console.log('Updating user with data:', { email, backendUserData });
        const response = await adminApi.put(`/auth/admin/users/${encodedEmail}`, backendUserData);
        return response.data;
    } catch (error) {
        console.error('Update user error:', error);
        const errorData = error.response?.data || {};
        const errorMessage = errorData.message || error.message || 'Failed to update user';
        const errorToThrow = new Error(errorMessage);
        errorToThrow.response = error.response;
        throw errorToThrow;
    }
};

export const deleteUser = async (userId) => {
    try {
        const response = await adminApi.delete(`/auth/admin/users/${userId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Streamer Management
export const getAllStreamers = async (filters = {}) => {
    try {
        const response = await adminApi.get('/auth/admin/streamers', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const addStreamer = async (streamerData) => {
    try {
        const response = await adminApi.post('/auth/admin/streamers', streamerData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateStreamer = async (streamerId, streamerData) => {
    try {
        const response = await adminApi.put(`/auth/admin/streamers/${streamerId}`, streamerData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteStreamer = async (streamerId) => {
    try {
        const response = await adminApi.delete(`/auth/admin/streamers/${streamerId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Card Management
export const getAllCardsAdmin = async (filters = {}) => {
    try {
        const response = await adminApi.get('/auth/admin/cards', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const addCardAdmin = async (cardData) => {
    try {
        const response = await adminApi.post('/auth/admin/cards', cardData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateCardAdmin = async (cardId, cardData) => {
    try {
        const response = await adminApi.put(`/auth/admin/cards/${cardId}`, cardData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteCardAdmin = async (cardId) => {
    try {
        const response = await adminApi.delete(`/auth/admin/cards/${cardId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Broadcast Card Management
export const getAllBroadcastCardsAdmin = async (filters = {}, page = 1, limit = 20) => {
    try {
        const response = await adminApi.get('/auth/admin/broadcast-cards', {
            params: { ...filters, page, limit }
        });
        // backend returns { success, cards, pagination }
        const data = response.data || {};
        return {
            success: !!data.success,
            cards: Array.isArray(data.cards) ? data.cards : [],
            pagination: data.pagination || { currentPage: 1, totalPages: 1, totalCards: 0 }
        };
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createBroadcastCardAdmin = async (cardData) => {
    try {
        const response = await adminApi.post('/auth/admin/broadcast-cards', cardData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateBroadcastCardAdmin = async (cardId, cardData) => {
    try {
        const response = await adminApi.put(`/auth/admin/broadcast-cards/${cardId}`, cardData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteBroadcastCardAdmin = async (cardId) => {
    try {
        const response = await adminApi.delete(`/auth/admin/broadcast-cards/${cardId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Dashboard Statistics
export const getDashboardStats = async () => {
    try {
        const response = await adminApi.get('/auth/admin/dashboard/stats');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getRecentActivity = async () => {
    try {
        const response = await adminApi.get('/auth/admin/dashboard/activity');
        // Ensure we return an array
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching recent activity:', error);
        return []; // Return empty array on error
    }
};

export const uploadFavicon = async (file) => {
    try {
        const formData = new FormData();
        formData.append('favicon', file);

        const response = await adminApi.post('/auth/admin/upload-favicon', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

export const getActiveTransactions = async () => {
    try {
        const response = await adminApi.get('/auth/admin/transactions/active');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getAllTransactions = async () => {
    try {
        const response = await adminApi.get('/auth/admin/transactions');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteTransaction = async (transactionId) => {
    try {
        const response = await adminApi.delete(`/auth/admin/transactions/${transactionId}`);
        return response.data;
    } catch (error) {
        if (error.response?.data) {
            throw error.response.data;
        }
        throw new Error('Failed to delete transaction');
    }
};

export const getTotalRevenue = async () => {
    try {
        const response = await adminApi.get('/auth/admin/transactions/revenue');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

